import Layout from "@/components/layout/Layout";
import React from "react";
import Content from "@/components/layout/Content";
import ContentOptions from "@/components/layout/ContentOptions";
import Heading from "@/components/elements/Heading";
import Sidepanel from "@/components/layout/Sidepanel";
import DashboardMenu from "@/pages/Dashboard/DashboardMenu";
import ReactHtmlParser from "react-html-parser";
import { usePage } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import { getHeaders } from "@/services/api";

/**
 * Order Dashboard Component
 *
 * Displays detailed information about a single order including customer details,
 * order metadata, and product configurations. Provides functionality to export,
 * duplicate, and edit orders.
 *
 * @param {Object} props - Component props
 * @param {Object} props.order - The order object to display
 * @param {string} props.order.id - Order ID
 * @param {string} props.order.created_at_nl - Formatted order date
 * @param {string} props.order.description - Order reference/description
 * @param {Object} props.order.contact - Customer contact information
 * @param {Object} props.order.contact.company - Company details
 * @param {string} props.order.contact.company.name - Company name
 * @param {Array} props.order.products - Array of products in the order
 * @param {string} props.duplicateOrderUrl - API endpoint for duplicating the order
 * @param {string} props.editOrderUrl - API endpoint for editing the order
 *
 * @returns {JSX.Element} The rendered order details page
 */
export default function Order({ order, duplicateOrderUrl, editOrderUrl }) {
    // Get authenticated user data from Inertia page props
    const { auth } = usePage().props;

    /**
     * Generate detailed HTML content for product configuration display
     * Creates formatted information about product layers, dimensions, and finishing
     *
     * @param {Object} product - Product object with configuration details
     * @param {Object} product.products - Product layer information (BU, IS1, TL, IS2, BI)
     * @param {Object} product.properties - Product properties and settings
     * @param {Array} product.variations - Product variations with dimensions
     * @param {Object} product.variations[].properties - Variation properties (DIKTE, TL_POS)
     *
     * @returns {string} HTML string with formatted product information
     */
    function getProductData(product) {
        let informationString =
            '<p style="line-height: 150%" class="productcontent">';

        // Product layer mapping for display names
        let productsArray = {
            BU: "Buitenlaag",
            IS1: "Isolatie (1)",
            TL: "Kernplaat",
            IS2: "Isolatie (2)",
            BI: "Binnenlaag",
        };

        // Build layer information with optional color codes
        for (let key in productsArray) {
            if (
                productsArray.hasOwnProperty(key) &&
                product.products &&
                product.products.hasOwnProperty(key) &&
                product.products[key]
            ) {
                informationString += `<b>${productsArray[key]}:</b> ${product.products[key].name} ${(key === "BU" || key === "BI") && product.properties && product.properties["KL_" + key] ? "(RAL" + product.properties["KL_" + key] + ")" : ""}<br/>`;
            }
        }

        // Add dimensional information
        informationString += `<br/><b>Dikte:</b> ${product.variations[0] ? product.variations[0].properties.DIKTE : 0}mm`;
        informationString += `<br/><b>Positie kernplaat:</b> ${product.variations[0] ? product.variations[0].properties.TL_POS : 0}mm`;
        informationString += `<br/><b>Sponning gewenst:</b> ${product.properties && product.properties["toon_afwerking"] ? "Ja" : "Nee"}<br/>`;

        // Add finishing/groove information if applicable
        if (product.properties && product.properties.toon_afwerking) {
            if (product.properties.randen_afwerking_gelijk) {
                // Equal finishing on all sides
                informationString += `<b>Alle randen gelijke sponning/randafwerking:</b> Ja<br/>`;
                informationString += `<b>Sponning/randafwerking:</b> ${product.properties.sponning_rondom === "SP_BU" ? "Buiten" : "Binnen"}<br/>`;
                informationString += `<b>Sponning/randafwerking breedte:</b> ${product.properties.sponning_rondom_breedte} mm<br/>`;
                informationString += `<b>Sponning/randafwerking hoogte:</b> ${product.properties.sponning_rondom_hoogte} mm<br/>`;
            } else {
                // Individual side finishing details
                informationString += `<b>Sponning/randafwerking links:</b> ${product.properties.sponning_links === "SP_BU" ? "Buiten" : "Binnen"} - Breedte: ${product.properties[`${product.properties.sponning_links}_LI_BR`]} mm - Hoogte: ${product.properties[`${product.properties.sponning_links}_LI_DI`]} mm<br/>`;
                informationString += `<b>Sponning/randafwerking rechts:</b> ${product.properties.sponning_rechts === "SP_BU" ? "Buiten" : "Binnen"} - Breedte: ${product.properties[`${product.properties.sponning_rechts}_RE_BR`]} mm - Hoogte: ${product.properties[`${product.properties.sponning_rechts}_RE_DI`]} mm<br/>`;
                informationString += `<b>Sponning/randafwerking boven:</b> ${product.properties.sponning_boven === "SP_BU" ? "Buiten" : "Binnen"} - Breedte: ${product.properties[`${product.properties.sponning_boven}_BO_BR`]} mm - Hoogte: ${product.properties[`${product.properties.sponning_boven}_BO_DI`]} mm<br/>`;
                informationString += `<b>Sponning/randafwerking onder:</b> ${product.properties.sponning_onder === "SP_BU" ? "Buiten" : "Binnen"} - Breedte: ${product.properties[`${product.properties.sponning_onder}_O_BR`]} mm - Hoogte: ${product.properties[`${product.properties.sponning_onder}_O_DI`]} mm<br/>`;
            }
        }

        informationString += "</p>";
        return informationString;
    }

    /**
     * Handle order duplication
     * Creates a copy of the current order with the current user as owner
     */
    function duplicateOrder() {
        return Inertia.post(duplicateOrderUrl, { user_id: auth.user.id }, {headers: getHeaders()});
    }

    /**
     * Handle order editing
     * Initiates order editing mode with the current user
     */
    function editOrder() {
        return Inertia.post(editOrderUrl, { user_id: auth.user.id }, {headers: getHeaders()});
    }

    return (
        <Layout>
            <Sidepanel>
                <DashboardMenu active={"Orders"} />
            </Sidepanel>
            <Content>
                <ContentOptions small>
                    <Heading heading={"Orders"} />
                </ContentOptions>
                <div
                    className={`contentoptions ingaveoptions col-md-9 d-flex flex-column`}
                >
                    <Heading heading="Order">
                        <div>
                            <a
                                name={"Order exporteren"}
                                href={`/export/${order.id}`}
                                target="_blank"
                                className="btn-orange mt-0"
                            >
                                Order exporteren
                            </a>
                            <a
                                name={"Order dupliceren"}
                                href="#"
                                onClick={duplicateOrder}
                                className="btn-orange mt-0"
                            >
                                Order dupliceren
                            </a>
                            <a
                                name={"Order aanpassen"}
                                href="#"
                                onClick={editOrder}
                                className="btn-orange mt-0"
                            >
                                Order aanpassen
                            </a>
                        </div>
                    </Heading>

                    <table className="table tablecustom tableadmin">
                        <tbody>
                            <tr>
                                <th>Besteldatum</th>
                                <td>{order.created_at_nl}</td>
                            </tr>
                            <tr>
                                <th>Bedrijfsnaam</th>
                                <td>{order.contact.company.name}</td>
                            </tr>
                            <tr>
                                <th>Referentie</th>
                                <td>{order.description}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="singleorderwrapper">
                        <b>Producten</b>
                        <br />
                        {order.products.map((product, key) => (
                            <div key={key}>
                                <hr />
                                <div className="product">
                                    <b>{product.product_name} informatie</b>
                                    {ReactHtmlParser(getProductData(product))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </Content>
        </Layout>
    );
}
